import React, { useState } from "react";
import axios from "axios";
import {
  Mail,
  Phone,
  MapPin,
  CalendarClock,
  Send,
  Loader2,
  CheckCircle2,
  Linkedin,
  Twitter,
  Github,
} from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Eyebrow } from "../components/Reveal";
import { CONTACT } from "../data/modules";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
  "Book a demo",
  "Pricing & plans",
  "Technical questions",
  "Partnership",
  "General inquiry",
  "Support",
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact Velacin",
  mainEntity: {
    "@type": "Organization",
    name: "Velacin",
    email: "hello@velacin.com",
    telephone: "+1-555-010-0100",
  },
};

const social = [
  { href: CONTACT.linkedin, label: "LinkedIn", Icon: Linkedin },
  { href: CONTACT.twitter, label: "Twitter", Icon: Twitter },
  { href: CONTACT.github, label: "GitHub", Icon: Github },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: "Book a demo",
    message: "",
    website: "", // honeypot
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!form.email.trim()) return "Please enter your work email.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Please enter a valid email address.";
    if (!form.message.trim()) return "Please tell us a little about your team.";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError("");
    setStatus("loading");
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      if (data?.ok) {
        setStatus("success");
      } else {
        throw new Error("Bad response");
      }
    } catch {
      setStatus("error");
      setError(
        "Something went wrong. Please try again or email us directly."
      );
    }
  };

  const reset = () => {
    setForm({
      name: "",
      email: "",
      company: "",
      phone: "",
      subject: "Book a demo",
      message: "",
      website: "",
    });
    setStatus("idle");
    setError("");
  };

  return (
    <>
      <Seo
        title="Contact — Velacin"
        description="Get in touch with the Velacin team. Book a demo, ask about pricing, or reach us for partnerships and support."
        path="/contact"
        jsonLd={jsonLd}
      />

      <section className="bg-black pt-36 pb-24 md:pt-44 md:pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Contact</Eyebrow>
            <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Let's build something.
            </h1>
            <p className="mt-6 max-w-2xl text-base text-white/60 md:text-lg">
              Book a demo, ask a technical question, or just say hi. We answer
              every message within one business day.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-10 lg:grid-cols-5 lg:gap-14">
            {/* Form */}
            <div className="lg:col-span-3">
              <Reveal>
                <div className="card-surface rounded-3xl p-7 md:p-9">
                  {status === "success" ? (
                    <div
                      data-testid="contact-success"
                      className="flex flex-col items-center py-10 text-center"
                      aria-live="polite"
                    >
                      <CheckCircle2 className="h-14 w-14 text-[#4C6EF5]" />
                      <h2 className="mt-5 text-2xl font-bold text-white">
                        Message received.
                      </h2>
                      <p className="mt-3 max-w-md text-sm text-white/60">
                        Thanks for reaching out. A human will read this and
                        respond within one business day.
                      </p>
                      <button
                        type="button"
                        onClick={reset}
                        data-testid="send-another"
                        className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-white/40 hover:bg-white/5"
                      >
                        Send another message
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={onSubmit} data-testid="contact-form" noValidate>
                      {/* Honeypot */}
                      <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={update("website")}
                        aria-hidden="true"
                        style={{ position: "absolute", left: "-9999px" }}
                      />

                      <div className="grid gap-5 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="name" className="text-white/80">
                            Name *
                          </Label>
                          <Input
                            id="name"
                            data-testid="contact-name"
                            placeholder="Jane Cooper"
                            value={form.name}
                            onChange={update("name")}
                            className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-white/80">
                            Work Email *
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            data-testid="contact-email"
                            placeholder="jane@company.com"
                            value={form.email}
                            onChange={update("email")}
                            className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30"
                          />
                        </div>
                        <div>
                          <Label htmlFor="company" className="text-white/80">
                            Company
                          </Label>
                          <Input
                            id="company"
                            data-testid="contact-company"
                            placeholder="Acme Inc."
                            value={form.company}
                            onChange={update("company")}
                            className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30"
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone" className="text-white/80">
                            Phone
                          </Label>
                          <Input
                            id="phone"
                            data-testid="contact-phone"
                            placeholder="+1 (555) 000-0000"
                            value={form.phone}
                            onChange={update("phone")}
                            className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30"
                          />
                        </div>
                      </div>

                      <div className="mt-5">
                        <Label className="text-white/80">Subject</Label>
                        <Select
                          value={form.subject}
                          onValueChange={update("subject")}
                        >
                          <SelectTrigger
                            data-testid="contact-subject"
                            className="mt-2 border-white/15 bg-white/5 text-white"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="border-white/15 bg-[#0A0A0A] text-white">
                            {SUBJECTS.map((s) => (
                              <SelectItem key={s} value={s}>
                                {s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="mt-5">
                        <Label htmlFor="message" className="text-white/80">
                          Message *
                        </Label>
                        <Textarea
                          id="message"
                          data-testid="contact-message"
                          rows={5}
                          placeholder="Tell us about your team..."
                          value={form.message}
                          onChange={update("message")}
                          className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30"
                        />
                      </div>

                      <div aria-live="assertive">
                        {error && (
                          <p
                            data-testid="contact-error"
                            className="mt-5 text-sm font-medium text-red-400"
                          >
                            {error}
                          </p>
                        )}
                      </div>

                      <button
                        type="submit"
                        data-testid="contact-submit"
                        disabled={status === "loading"}
                        className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3 text-sm font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-60"
                      >
                        {status === "loading" ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4" />
                            Send message
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </Reveal>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-2">
              <Reveal delay={0.1}>
                <div className="space-y-8">
                  <div>
                    <h3 className="font-mono-label text-xs uppercase tracking-widest text-white/50">
                      Reach us directly
                    </h3>
                    <ul className="mt-5 space-y-4 text-sm">
                      <li className="flex items-start gap-3">
                        <Mail className="mt-0.5 h-5 w-5 text-[#8FA4FF]" />
                        <div>
                          <div className="text-white/40">Email</div>
                          <a
                            href={`mailto:${CONTACT.emailSales}`}
                            className="text-white hover:text-[#8FA4FF]"
                          >
                            {CONTACT.emailSales}
                          </a>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Phone className="mt-0.5 h-5 w-5 text-[#8FA4FF]" />
                        <div>
                          <div className="text-white/40">Phone</div>
                          <a
                            href={`tel:${CONTACT.phone}`}
                            className="text-white hover:text-[#8FA4FF]"
                          >
                            {CONTACT.phone}
                          </a>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <MapPin className="mt-0.5 h-5 w-5 text-[#8FA4FF]" />
                        <div>
                          <div className="text-white/40">HQ</div>
                          <span className="text-white">{CONTACT.hq}</span>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <CalendarClock className="mt-0.5 h-5 w-5 text-[#8FA4FF]" />
                        <div>
                          <div className="text-white/40">Book a call</div>
                          <span className="text-white">
                            30-min product walkthrough
                          </span>
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-mono-label text-xs uppercase tracking-widest text-white/50">
                      Follow along
                    </h3>
                    <div className="mt-4 flex gap-3">
                      {social.map(({ href, label, Icon }) => (
                        <a
                          key={label}
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={label}
                          className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-white/70 transition-colors hover:border-[#4C6EF5]/60 hover:text-white"
                        >
                          <Icon className="h-4 w-4" />
                        </a>
                      ))}
                    </div>
                  </div>

                  <div className="card-surface rounded-2xl p-6">
                    <p className="text-sm leading-relaxed text-white/70">
                      <span className="font-semibold text-white">
                        Response time:
                      </span>{" "}
                      We answer every message within one business day. For urgent
                      matters, email us directly with "Urgent" in the subject
                      line.
                    </p>
                  </div>
                </div>
              </Reveal>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}
